import { useState, useEffect, useMemo, useRef } from "react";
import { useAuth } from "@/hooks/use-auth";
import { useIsAdmin } from "@/hooks/useIsAdmin";
import { useQuery, useMutation } from "convex/react";
import { api } from "../../../convex/_generated/api";
import { Id } from "../../../convex/_generated/dataModel";
import { Link, useRoute, useLocation } from "wouter";
import {
  Stethoscope,
  UserRound,
  LogOut,
  Loader2,
  FileText,
  LayoutDashboard,
  MessageSquare,
  Globe,
  Plus,
  Trash2,
  Save,
  X,
  Tag as TagIcon,
  Check,
  Shield,
  Settings as SettingsIcon,
} from "lucide-react";
import { Button } from "@/components/ui/button";
import { ScrollArea } from "@/components/ui/scroll-area";
import { Avatar, AvatarFallback, AvatarImage } from "@/components/ui/avatar";
import { Input } from "@/components/ui/input";
import {
  AlertDialog,
  AlertDialogAction,
  AlertDialogCancel,
  AlertDialogContent,
  AlertDialogDescription,
  AlertDialogFooter,
  AlertDialogHeader,
  AlertDialogTitle,
} from "@/components/ui/alert-dialog";
import { useToast } from "@/hooks/use-toast";
import { ThemeToggle } from "@/components/ThemeToggle";
import {
  DropdownMenu,
  DropdownMenuContent,
  DropdownMenuItem,
  DropdownMenuLabel,
  DropdownMenuSeparator,
  DropdownMenuTrigger,
} from "@/components/ui/dropdown-menu";
import { SEO } from "@/components/SEO";
import { EditHistoryPanel } from "@/components/EditHistoryPanel";
import { History } from "lucide-react";

export default function GlobalRules() {
  const { user, logout, isLoading: authLoading } = useAuth();
  const { toast } = useToast();
  const [, navigate] = useLocation();
  const [matchRulesRoute, rulesRouteParams] = useRoute<{ id: string }>("/rules/:id");
  const rawRouteTagId = matchRulesRoute ? rulesRouteParams?.id ?? null : null;
  const routeTagId =
    rawRouteTagId && /^[a-z0-9_]{16,}$/i.test(rawRouteTagId)
      ? (rawRouteTagId as Id<"ruleTags">)
      : null;
  const [userSynced, setUserSynced] = useState(false);
  const [selectedTagId, setSelectedTagIdState] = useState<Id<"ruleTags"> | null>(
    routeTagId,
  );
  const setSelectedTagId = (id: Id<"ruleTags"> | null) => {
    setSelectedTagIdState(id);
    if (id) {
      if (routeTagId !== id) navigate(`/rules/${id}`);
    } else if (matchRulesRoute) {
      navigate("/rules");
    }
  };
  const [historyOpen, setHistoryOpen] = useState(false);
  const [showNewTagInput, setShowNewTagInput] = useState(false);
  const [newTagName, setNewTagName] = useState("");
  const [isCreatingTag, setIsCreatingTag] = useState(false);
  const [deletingTagId, setDeletingTagId] = useState<Id<"ruleTags"> | null>(null);
  const [content, setContent] = useState("");
  const [savedContent, setSavedContent] = useState("");
  const [isSaving, setIsSaving] = useState(false);
  const loadedForTagRef = useRef<string | null>(null);

  const getOrCreateUser = useMutation(api.users.getOrCreateUser);
  const setTagContent = useMutation(api.globalRules.setTagContent);
  const ensureGlobalTag = useMutation(api.ruleTags.ensureGlobal);
  const createTag = useMutation(api.ruleTags.create);
  const removeTag = useMutation(api.ruleTags.remove);

  useEffect(() => {
    if (user && !userSynced) {
      getOrCreateUser({
        googleId: user.id,
        email: user.email || "",
        name: user.firstName
          ? `${user.firstName} ${user.lastName || ""}`.trim()
          : undefined,
        profileImageUrl: user.profileImageUrl || undefined,
      })
        .then(() => setUserSynced(true))
        .catch((err) => console.error("Failed to sync user with Convex:", err));
    }
  }, [user, userSynced, getOrCreateUser]);

  const convexUser = useQuery(
    api.users.getCurrentUser,
    user && userSynced ? { googleId: user.id } : "skip",
  );
  const isAdmin = useIsAdmin();
  const allRules = useQuery(
    api.globalRules.list,
    convexUser ? { userId: convexUser._id } : "skip",
  );

  const tags = useQuery(
    api.ruleTags.list,
    convexUser ? { userId: convexUser._id } : "skip",
  );

  useEffect(() => {
    if (convexUser && tags !== undefined && tags.length === 0) {
      ensureGlobalTag({ userId: convexUser._id }).catch((err) =>
        console.error("Failed to ensure Global tag:", err),
      );
    }
  }, [convexUser, tags, ensureGlobalTag]);

  const rulesByTag = useMemo(() => {
    const map = new Map<string, typeof allRules>();
    (allRules || []).forEach((r) => {
      if (!r.tagId) return;
      const key = String(r.tagId);
      const arr = map.get(key) || [];
      arr!.push(r);
      map.set(key, arr);
    });
    return map;
  }, [allRules]);

  useEffect(() => {
    if (!tags || tags.length === 0) return;
    // If URL specifies a tag id that belongs to the user, select it.
    if (routeTagId && tags.some((t) => t._id === routeTagId)) {
      if (selectedTagId !== routeTagId) {
        loadedForTagRef.current = null;
        setSelectedTagIdState(routeTagId);
      }
      return;
    }
    // URL had an invalid/unauthorized id — correct it to /rules.
    if (matchRulesRoute && rawRouteTagId && !routeTagId) {
      navigate("/rules", { replace: true });
    } else if (matchRulesRoute && routeTagId && !tags.some((t) => t._id === routeTagId)) {
      navigate("/rules", { replace: true });
    }
    if (selectedTagId && tags.some((t) => t._id === selectedTagId)) return;
    const globalTag = tags.find((t) => t.normalizedName === "global");
    const fallbackId = (globalTag?._id || tags[0]._id) as Id<"ruleTags">;
    loadedForTagRef.current = null;
    setSelectedTagIdState(fallbackId);
  }, [tags, selectedTagId, routeTagId, matchRulesRoute, rawRouteTagId, navigate]);

  const selectedTag = useMemo(
    () => (tags || []).find((t) => t._id === selectedTagId) || null,
    [tags, selectedTagId],
  );

  const rulesForSelectedTag = useMemo(() => {
    if (!selectedTagId) return [];
    return rulesByTag.get(String(selectedTagId)) || [];
  }, [rulesByTag, selectedTagId]);

  // Load content when selected tag changes (or its rules first arrive).
  useEffect(() => {
    if (!selectedTagId || allRules === undefined) return;
    const key = String(selectedTagId);
    if (loadedForTagRef.current === key) return;
    const rules = rulesByTag.get(key) || [];
    const joined = rules.map((r) => r!.content).join("\n\n");
    setContent(joined);
    setSavedContent(joined);
    loadedForTagRef.current = key;
  }, [selectedTagId, allRules, rulesByTag]);

  if (authLoading) {
    return (
      <div className="min-h-screen flex items-center justify-center bg-background">
        <Loader2 className="h-8 w-8 animate-spin text-primary" />
      </div>
    );
  }

  if (!user) {
    window.location.href = "/api/login";
    return null;
  }

  if (!userSynced || !convexUser) {
    return (
      <div className="min-h-screen flex items-center justify-center bg-background">
        <Loader2 className="h-8 w-8 animate-spin text-primary" />
      </div>
    );
  }

  const isDirty = content !== savedContent;

  const handleSave = async () => {
    if (!selectedTagId) return;
    if (isSaving) return;
    setIsSaving(true);
    const snapshot = content;
    const tagAtSave = selectedTagId;
    try {
      await setTagContent({
        userId: convexUser._id,
        tagId: tagAtSave,
        content: snapshot,
      });
      // Only commit savedContent if the user is still on the same tag and
      // hasn't typed past the snapshot we sent.
      setSavedContent((prev) =>
        selectedTagId === tagAtSave && content === snapshot ? snapshot : prev,
      );
      toast({ title: "Saved" });
    } catch (err) {
      console.error(err);
      toast({
        title: "Failed to save",
        description: err instanceof Error ? err.message : "Error saving rules.",
        variant: "destructive",
      });
    } finally {
      setIsSaving(false);
    }
  };

  const confirmDiscardIfDirty = () => {
    if (!isDirty) return true;
    return window.confirm(
      "You have unsaved changes. Discard them and continue?",
    );
  };

  const handleSelectTag = (id: Id<"ruleTags">) => {
    if (!confirmDiscardIfDirty()) return;
    loadedForTagRef.current = null;
    setSelectedTagId(id);
  };

  const handleCreateTag = async () => {
    const name = newTagName.trim();
    if (!name) return;
    if (!confirmDiscardIfDirty()) return;
    const normalized = name.toLowerCase();
    const existing = tags?.find((t) => t.normalizedName === normalized);
    if (existing) {
      loadedForTagRef.current = null;
      setSelectedTagId(existing._id);
      setShowNewTagInput(false);
      setNewTagName("");
      toast({
        title: "Tag already exists",
        description: `Switched to existing tag "${existing.name}".`,
      });
      return;
    }
    setIsCreatingTag(true);
    try {
      const tagId = await createTag({ userId: convexUser._id, name });
      loadedForTagRef.current = null;
      setSelectedTagId(tagId);
      setShowNewTagInput(false);
      setNewTagName("");
      toast({ title: "Tag created", description: `"${name}" added.` });
    } catch (err) {
      toast({
        title: "Failed to create tag",
        description: err instanceof Error ? err.message : "Error",
        variant: "destructive",
      });
    } finally {
      setIsCreatingTag(false);
    }
  };

  const handleDeleteTag = async () => {
    if (!deletingTagId) return;
    try {
      await removeTag({ id: deletingTagId, userId: convexUser._id });
      if (selectedTagId === deletingTagId) {
        const globalTag = (tags || []).find((t) => t.normalizedName === "global");
        loadedForTagRef.current = null;
        setSelectedTagId(globalTag?._id || null);
      }
      toast({ title: "Tag deleted" });
    } catch (err) {
      toast({
        title: "Failed to delete tag",
        description: err instanceof Error ? err.message : "Error",
        variant: "destructive",
      });
    } finally {
      setDeletingTagId(null);
    }
  };

  const deletingTag = tags?.find((t) => t._id === deletingTagId);
  const hasRules = (id: Id<"ruleTags">) =>
    (rulesByTag.get(String(id)) || []).length > 0;

  return (
    <div className="h-screen flex flex-col bg-background">
      <SEO title="Global Rules — LRScribe" noindex />
      <header className="relative h-[26px] border-b border-border bg-card flex items-center justify-between px-2 shrink-0 gap-2">
        <div className="flex items-center gap-1.5 min-w-0">
          <Link
            href="/dashboard"
            className="flex items-center gap-1 shrink-0"
            aria-label="LRScribe home"
          >
            <div className="p-0.5 bg-primary/10 rounded-sm">
              <Stethoscope className="w-3 h-3 text-primary" />
            </div>
            <span className="font-heading font-bold text-xs text-foreground hidden sm:inline leading-none">
              LRScribe
            </span>
          </Link>
        </div>

        <nav className="absolute left-1/2 -translate-x-1/2 flex items-center gap-3">
          <Link
            href="/dashboard"
            className="text-xs font-medium text-muted-foreground hover:text-foreground transition-colors flex items-center gap-1 leading-none"
            data-testid="link-dashboard"
          >
            <LayoutDashboard className="w-3.5 h-3.5" />
            <span className="hidden sm:inline">Dashboard</span>
          </Link>
          <Link
            href="/templates"
            className="text-xs font-medium text-muted-foreground hover:text-foreground transition-colors flex items-center gap-1 leading-none"
            data-testid="link-templates"
          >
            <FileText className="w-3.5 h-3.5" />
            <span className="hidden sm:inline">Templates</span>
          </Link>
          <Link
            href="/rules"
            className="text-xs font-medium text-primary transition-colors flex items-center gap-1 leading-none"
            data-testid="link-rules"
          >
            <Globe className="w-3.5 h-3.5" />
            <span className="hidden sm:inline">Rules</span>
          </Link>
          <Link
            href="/profiles"
            className="text-xs font-medium text-muted-foreground hover:text-foreground transition-colors flex items-center gap-1 leading-none"
            data-testid="link-profiles"
          >
            <UserRound className="w-3.5 h-3.5" />
            <span className="hidden sm:inline">Profiles</span>
          </Link>
          <Link
            href="/chat"
            className="text-xs font-medium text-muted-foreground hover:text-foreground transition-colors flex items-center gap-1 leading-none"
            data-testid="link-chat"
          >
            <MessageSquare className="w-3.5 h-3.5" />
            <span className="hidden sm:inline">Chat</span>
          </Link>
        </nav>

        <div className="flex items-center gap-0.5 shrink-0">
          <ThemeToggle className="h-5 w-5 [&_svg]:w-3.5 [&_svg]:h-3.5" />
          <DropdownMenu>
            <DropdownMenuTrigger asChild>
              <button
                type="button"
                aria-label="Open user menu"
                className="flex items-center rounded-full outline-none focus-visible:ring-2 focus-visible:ring-ring focus-visible:ring-offset-1 focus-visible:ring-offset-background cursor-pointer"
                data-testid="button-user-menu"
              >
                <Avatar className="h-5 w-5 pointer-events-none">
                  <AvatarImage src={user.profileImageUrl || undefined} />
                  <AvatarFallback className="bg-primary/10 text-primary text-[10px]">
                    {user.firstName?.charAt(0) || user.email?.charAt(0) || "U"}
                  </AvatarFallback>
                </Avatar>
              </button>
            </DropdownMenuTrigger>
            <DropdownMenuContent align="end" className="w-56">
              <DropdownMenuLabel className="font-normal">
                <div
                  className="text-sm font-medium text-foreground truncate"
                  data-testid="text-user-name"
                >
                  {user.firstName
                    ? `${user.firstName} ${user.lastName || ""}`.trim()
                    : user.email}
                </div>
              </DropdownMenuLabel>
              <DropdownMenuSeparator />
              <DropdownMenuItem asChild>
                <Link href="/settings" data-testid="link-settings">
                  <SettingsIcon className="w-4 h-4 mr-2" />
                  Settings
                </Link>
              </DropdownMenuItem>
              <DropdownMenuItem asChild>
                <Link href="/security" data-testid="link-security">
                  <Shield className="w-4 h-4 mr-2" />
                  Security
                </Link>
              </DropdownMenuItem>
              <DropdownMenuItem asChild>
                <Link href="/change-log" data-testid="link-change-log">
                  <History className="w-4 h-4 mr-2" />
                  Change log
                </Link>
              </DropdownMenuItem>
              {isAdmin && (
                <DropdownMenuItem asChild>
                  <Link href="/admin/settings" data-testid="link-admin">
                    <Shield className="w-4 h-4 mr-2" />
                    Admin
                  </Link>
                </DropdownMenuItem>
              )}
              <DropdownMenuSeparator />
              <DropdownMenuItem
                onClick={() => logout()}
                className="text-destructive focus:text-destructive"
                data-testid="button-logout"
              >
                <LogOut className="w-4 h-4 mr-2" />
                Log out
              </DropdownMenuItem>
            </DropdownMenuContent>
          </DropdownMenu>
        </div>
      </header>

      <div className="flex-1 flex min-h-0">
        {/* Left pane: tags */}
        <aside className="w-72 border-r border-border bg-card/30 flex flex-col shrink-0">
          <div className="p-4 border-b border-border flex items-center justify-between">
            <div>
              <h2 className="text-sm font-semibold text-foreground flex items-center gap-2">
                <TagIcon className="w-4 h-4" />
                Tags
              </h2>
              <p className="text-xs text-muted-foreground mt-1">
                {(tags || []).length} total
              </p>
            </div>
            {!showNewTagInput && (
              <Button
                size="sm"
                variant="outline"
                onClick={() => setShowNewTagInput(true)}
                data-testid="button-new-tag"
              >
                <Plus className="w-4 h-4 mr-1" />
                New
              </Button>
            )}
          </div>

          {showNewTagInput && (
            <div className="p-3 border-b border-border space-y-2 bg-muted/20">
              <Input
                value={newTagName}
                onChange={(e) => setNewTagName(e.target.value)}
                placeholder="e.g. HPI, Assessment"
                autoFocus
                onKeyDown={(e) => {
                  if (e.key === "Enter") handleCreateTag();
                  if (e.key === "Escape") {
                    setShowNewTagInput(false);
                    setNewTagName("");
                  }
                }}
                className="h-8 text-sm"
                data-testid="input-new-tag-name"
              />
              <div className="flex items-center gap-2">
                <Button
                  size="sm"
                  onClick={handleCreateTag}
                  disabled={!newTagName.trim() || isCreatingTag}
                  data-testid="button-confirm-new-tag"
                  className="flex-1"
                >
                  {isCreatingTag ? (
                    <Loader2 className="w-3 h-3 animate-spin" />
                  ) : (
                    "Create"
                  )}
                </Button>
                <Button
                  size="sm"
                  variant="ghost"
                  onClick={() => {
                    setShowNewTagInput(false);
                    setNewTagName("");
                  }}
                >
                  <X className="w-4 h-4" />
                </Button>
              </div>
            </div>
          )}

          <ScrollArea className="flex-1">
            {tags === undefined ? (
              <div className="flex items-center justify-center py-8">
                <Loader2 className="h-5 w-5 animate-spin text-muted-foreground" />
              </div>
            ) : tags.length === 0 ? (
              <p className="text-xs text-muted-foreground p-4 text-center">
                No tags yet.
              </p>
            ) : (
              <ul className="p-2 space-y-1">
                {tags.map((tag) => {
                  const isSelected = selectedTagId === tag._id;
                  const filled = hasRules(tag._id);
                  const isGlobal = tag.normalizedName === "global";
                  return (
                    <li key={tag._id}>
                      <button
                        type="button"
                        onClick={() => handleSelectTag(tag._id)}
                        className={`w-full text-left px-3 py-2 rounded-md text-sm transition-colors flex items-center justify-between group ${
                          isSelected
                            ? "bg-primary/10 text-primary border border-primary/30"
                            : "hover:bg-muted/50 text-foreground border border-transparent"
                        }`}
                        data-testid={`button-select-tag-${tag._id}`}
                      >
                        <span className="flex items-center gap-2 truncate min-w-0">
                          {isGlobal ? (
                            <Globe className="w-3.5 h-3.5 shrink-0" />
                          ) : (
                            <TagIcon className="w-3.5 h-3.5 shrink-0" />
                          )}
                          <span className="truncate">{tag.name}</span>
                        </span>
                        <span className="flex items-center gap-1 shrink-0">
                          {filled && (
                            <Check
                              className={`w-3.5 h-3.5 ${
                                isSelected
                                  ? "text-primary"
                                  : "text-muted-foreground"
                              }`}
                              data-testid={`icon-tag-filled-${tag._id}`}
                            />
                          )}
                          {!isGlobal && (
                            <span
                              role="button"
                              tabIndex={0}
                              onClick={(e) => {
                                e.stopPropagation();
                                setDeletingTagId(tag._id);
                              }}
                              onKeyDown={(e) => {
                                if (e.key === "Enter" || e.key === " ") {
                                  e.stopPropagation();
                                  setDeletingTagId(tag._id);
                                }
                              }}
                              className="opacity-0 group-hover:opacity-100 text-muted-foreground hover:text-destructive p-1 rounded cursor-pointer transition-opacity"
                              data-testid={`button-delete-tag-${tag._id}`}
                            >
                              <Trash2 className="w-3 h-3" />
                            </span>
                          )}
                        </span>
                      </button>
                    </li>
                  );
                })}
              </ul>
            )}
          </ScrollArea>
        </aside>

        {/* Right pane: single notepad-style editor for the selected tag */}
        <main className="flex-1 flex flex-col min-h-0">
          {selectedTag ? (
            <>
              <div className="px-8 pt-6 pb-4 border-b border-border bg-card/20 flex items-start justify-between gap-4">
                <div className="min-w-0">
                  <h1 className="text-xl font-bold text-foreground flex items-center gap-2">
                    {selectedTag.normalizedName === "global" ? (
                      <Globe className="w-5 h-5 text-primary" />
                    ) : (
                      <TagIcon className="w-5 h-5 text-primary" />
                    )}
                    <span className="truncate">{selectedTag.name}</span>
                  </h1>
                  <p className="text-xs text-muted-foreground mt-1">
                    {selectedTag.normalizedName === "global"
                      ? "Rules in this notepad apply to any section tagged Global."
                      : `Rules in this notepad apply to any section tagged "${selectedTag.name}".`}
                  </p>
                </div>
                <div className="flex items-center gap-3 shrink-0">
                  <span
                    className={`text-xs ${
                      isDirty
                        ? "text-amber-600 dark:text-amber-400"
                        : "text-muted-foreground"
                    }`}
                    data-testid="text-save-state"
                  >
                    {isSaving
                      ? "Saving…"
                      : isDirty
                        ? "Unsaved changes"
                        : "Saved"}
                  </span>
                  <Button
                    size="icon"
                    variant="ghost"
                    onClick={() => setHistoryOpen(true)}
                    className="h-8 w-8"
                    title="View edit history for this notepad"
                    data-testid="button-rules-history"
                  >
                    <History className="w-4 h-4" />
                  </Button>
                  <Button
                    size="sm"
                    onClick={handleSave}
                    disabled={!isDirty || isSaving}
                    data-testid="button-save-rules"
                  >
                    {isSaving ? (
                      <Loader2 className="w-4 h-4 mr-1 animate-spin" />
                    ) : (
                      <Save className="w-4 h-4 mr-1" />
                    )}
                    Save
                  </Button>
                </div>
              </div>

              <div className="flex-1 min-h-0 p-6 bg-background">
                <textarea
                  value={content}
                  onChange={(e) => setContent(e.target.value)}
                  onKeyDown={(e) => {
                    if ((e.ctrlKey || e.metaKey) && e.key === "s") {
                      e.preventDefault();
                      if (isDirty && !isSaving) handleSave();
                    }
                  }}
                  placeholder={`Write the rules for "${selectedTag.name}" here.\n\nFor example:\n- Use complete sentences.\n- Consolidate ideas into a single paragraph instead of allowing ideas to span paragraphs.\n- Avoid abbreviations unless they are standard medical terms.`}
                  className="w-full h-full resize-none rounded-md border border-border bg-card p-4 text-sm font-mono leading-relaxed text-foreground placeholder:text-muted-foreground focus:outline-none focus:ring-2 focus:ring-primary/40"
                  spellCheck={true}
                  data-testid="textarea-tag-rules"
                />
              </div>
            </>
          ) : (
            <div className="flex-1 flex items-center justify-center">
              <p className="text-sm text-muted-foreground">
                Select a tag from the left to edit its rules.
              </p>
            </div>
          )}
        </main>
      </div>

      <AlertDialog
        open={!!deletingTagId}
        onOpenChange={(open) => !open && setDeletingTagId(null)}
      >
        <AlertDialogContent>
          <AlertDialogHeader>
            <AlertDialogTitle>
              Delete tag "{deletingTag?.name}"?
            </AlertDialogTitle>
            <AlertDialogDescription>
              This tag will be removed from any template sections that use it.
              You cannot delete a tag while it still has rules — clear the
              rules first. This action cannot be undone.
            </AlertDialogDescription>
          </AlertDialogHeader>
          <AlertDialogFooter>
            <AlertDialogCancel data-testid="button-cancel-delete-tag">
              Cancel
            </AlertDialogCancel>
            <AlertDialogAction
              onClick={handleDeleteTag}
              className="bg-destructive text-destructive-foreground hover:bg-destructive/90"
              data-testid="button-confirm-delete-tag"
            >
              Delete
            </AlertDialogAction>
          </AlertDialogFooter>
        </AlertDialogContent>
      </AlertDialog>
      {convexUser && selectedTag && (
        <EditHistoryPanel
          open={historyOpen}
          onOpenChange={setHistoryOpen}
          userId={convexUser._id}
          entityType="globalRule"
          entityIds={rulesForSelectedTag.map((r) => String(r._id))}
          title={`Edit history — ${selectedTag.name}`}
          description="Track every change to the rules in this notepad."
          restoreGlobalRule={{
            ruleContents: Object.fromEntries(
              rulesForSelectedTag.map((r) => [String(r!._id), r!.content]),
            ),
            onRestored: () => {
              if (!isDirty) {
                loadedForTagRef.current = null;
              }
            },
          }}
        />
      )}
    </div>
  );
}
